import { CheckCircle2, Users, TrendingUp, Globe2, MapPin } from 'lucide-react';

const BENEFITS = [
  {
    Icon: Users,
    title: 'Strong Indian Diaspora',
    bodyStrong: '1.2 million',
    bodyRest: ' people of Indian origin live in ANZ, creating immediate brand recognition and trust for Indian products.',
    bodyPrefix: 'Over ',
    bullets: [
      'Built-in customer base familiar with Indian brands',
      'Word-of-mouth marketing within communities',
      'Cultural understanding of product benefits',
    ],
  },
  {
    Icon: TrendingUp,
    title: 'High Purchasing Power',
    bodyPrefix: 'ANZ consumers have among the ',
    bodyStrong: 'highest disposable incomes',
    bodyRest: ' globally, willing to pay premium prices for quality products.',
    bullets: [
      'Premium pricing acceptable for quality goods',
      'Strong demand for authentic ethnic products',
      'Growing health & wellness market',
    ],
  },
  {
    Icon: CheckCircle2,
    title: 'Transparent Regulations',
    bodyPrefix: 'Clear, ',
    bodyStrong: 'predictable regulatory framework',
    bodyRest: ' with no hidden barriers. What you see is what you get—no surprises.',
    bullets: [
      'Straightforward import and compliance process',
      'Strong IP protection and business laws',
      'English-speaking markets, easier navigation',
    ],
  },
];

const STATS = [
  { value: '$1.4T+', label: 'Combined GDP', sub: 'Wealthy, stable economies' },
  { value: '31M', label: 'Total Population', sub: 'Manageable market size' },
  { value: 'Top 20', label: 'Ease of Doing Business', sub: 'Both countries ranked globally' },
];

const BenefitCard = ({ Icon, title, bodyPrefix, bodyStrong, bodyRest, bullets }) => (
  <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg border-2 border-gray-100 hover:border-[#004aad] transition-all">
    <div className="flex items-start space-x-4">
      <div className="h-14 w-14 bg-[#004aad] rounded-xl flex items-center justify-center flex-shrink-0">
        <Icon className="h-7 w-7 text-white" />
      </div>
      <div className="flex-1">
        <h3 className="text-2xl font-bold text-gray-900 mb-3">{title}</h3>
        <p className="text-gray-600 mb-4 leading-relaxed">
          {bodyPrefix}<strong className="text-[#004aad]">{bodyStrong}</strong>{bodyRest}
        </p>
        <ul className="space-y-2 text-gray-600">
          {bullets.map((b) => (
            <li key={b} className="flex items-start space-x-2">
              <CheckCircle2 className="h-5 w-5 text-[#004aad] flex-shrink-0 mt-0.5" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

export const WhyAnz = () => (
  <section className="py-20 md:py-32 bg-white">
    <div className="container mx-auto px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-green-50 text-green-700 px-5 py-2 rounded-full text-sm font-semibold mb-6">
            <MapPin className="h-4 w-4" />
            <span>STRATEGIC MARKET CHOICE</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Why Australia & New Zealand?</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            ANZ isn't just another market—it's the <strong className="text-[#004aad]">smartest first international step</strong> for Indian brands. Lower risk, higher success rates, and a proven path to global expansion.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {BENEFITS.map((b) => <BenefitCard key={b.title} {...b} />)}

          <div className="bg-gradient-to-br from-[#004aad] to-[#0066cc] rounded-2xl p-8 shadow-xl border-2 border-[#004aad]">
            <div className="flex items-start space-x-4">
              <div className="h-14 w-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0">
                <Globe2 className="h-7 w-7 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-white mb-3">Gateway to Global Expansion</h3>
                <p className="text-blue-100 mb-4 leading-relaxed">
                  Success in ANZ opens doors to <strong className="text-white">UK, EU, North America</strong>. Use ANZ as your proof-of-concept for Western markets.
                </p>
                <ul className="space-y-2 text-white">
                  {['Similar consumer behavior to other Western markets', 'Validate product-market fit before larger investments', 'Build credibility for future global partnerships'].map((b) => (
                    <li key={b} className="flex items-start space-x-2">
                      <CheckCircle2 className="h-5 w-5 text-white flex-shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            {STATS.map((s) => (
              <div key={s.label}>
                <div className="text-3xl font-bold text-[#004aad] mb-2">{s.value}</div>
                <div className="text-gray-600 text-sm">{s.label}</div>
                <div className="text-gray-500 text-xs mt-1">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-lg text-gray-700 mb-6">
            <strong className="text-[#004aad]">Start smart. Start with ANZ.</strong> Test your product in a market that's proven to launch Indian brands successfully.
          </p>
        </div>
      </div>
    </div>
  </section>
);
